import { useWallet } from '@suiet/wallet-kit';
import React, { useContext, useEffect, useState } from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import '@mantine/core/styles.css';
import './App.css';

import Header from './components/header';
import '@suiet/wallet-kit/style.css';
import '../styles/wallet.css';
import { SnackbarProvider } from 'notistack';
import { useNetwork } from '../hooks/useNetwork';
import { getConsts } from 'sudo-ts-sdk';
import {
  IAccountData,
  IWalletStatus,
  UserSignature,
  useAccountData,
} from '../hooks/useAccountData';
import { useTokenPrice } from '../hooks/useTokenPrice';
import { createTheme, MantineProvider } from '@mantine/core';
import Button from './components/Button';
import { useOwnedObjects } from '../hooks/useOwnedObjects';
import { Loading } from './components/Base/Loading';
import { OwnedObjectType } from './components/Inventory/OwnedObjects';

const theme = createTheme({});

type ContextType = {
  wallet: ReturnType<typeof useWallet>;
  network: ReturnType<typeof useNetwork>;
  consts: ReturnType<typeof getConsts>;
  accountData: IAccountData;
  refreshAccount: () => void;
  prices: { [key: string]: number };
  priceLoading: boolean;
  walletStatus: IWalletStatus;
  userSignature: UserSignature;
  ownedSudoObjects: OwnedObjectType[];
  refetchOwnedObjects: () => void;
};

const App: React.FC = () => {
  const wallet = useWallet();
  const network = useNetwork();
  const consts = getConsts(network);
  const {
    accountData,
    refresh: refreshAccount,
    walletStatus,
    userSignature,
  } = useAccountData(wallet, network, consts);
  const { tokenPrice: prices, isLoading: priceLoading } =
    useTokenPrice(network);
  const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const {
    data: ownedSudoObjects,
    isLoading: isQueryingOwnedSudoObjects,
    refetch: refetchOwnedObjects,
  } = useOwnedObjects({ consts: consts, address: accountData.address });

  const handleClick = () => {
    window.location.href = 'https://app.sudo.finance/scard';
  };

  useEffect(() => {
    if (location.pathname === '/reward') {
      navigate('/reward', { replace: true });
    } else if (location.pathname === '/points') {
      navigate('/points', { replace: true });
    } else if (location.pathname === '/rank') {
      navigate('/rank', { replace: true });
    } else if (location.pathname === '/mint') {
      navigate('/mint', { replace: true });
    } else if (location.pathname === '/scard') {
      navigate('/scard', { replace: true });
    } else {
      navigate('/mint', { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (accountData.address) {
      refetchOwnedObjects();
    }
  }, [accountData.address, refetchOwnedObjects, ownedSudoObjects]);

  if (isQueryingOwnedSudoObjects) {
    return <Loading />;
  }

  return (
    <MantineProvider theme={theme} defaultColorScheme="dark">
      <SnackbarProvider anchorOrigin={{ horizontal: 'right', vertical: 'top' }}>
        {isLoading ? (
          <div className="global-container loader-container">
            <div className="loader-content">
              <Button
                buttonStyle={'main'}
                text={'Enter S'}
                onClick={() => {
                  handleClick();
                }}
                isLoading={false}
                className="app-loader-button"
              />
            </div>
          </div>
        ) : (
          <div className="global-container app-container">
            <Header />
            <Outlet
              context={{
                wallet,
                network,
                consts,
                accountData,
                refreshAccount,
                prices,
                priceLoading,
                walletStatus,
                userSignature,
                ownedSudoObjects,
                refetchOwnedObjects,
              }}
            />
          </div>
        )}
      </SnackbarProvider>
    </MantineProvider>
  );
};

export function useEssential() {
  return useOutletContext<ContextType>();
}

export default App;
